import { Column } from '@ant-design/charts';
import { formatDate, formatDateTimeChart, useTranslation } from '@threatadvisor/react-utils';
import { Spin, Typography } from 'antd';
import React, { ReactElement, useEffect, useId } from 'react';
import { ResultSet } from '@cubejs-client/core';
import { useCubeQuery } from '@cubejs-client/react';
import { getPriorityScaleColors } from '../../utils';
import { VizState } from '@threatadvisor/react-components';

export type AverageTimeToActionByPrioritytProps = {
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  securityEventApi: string;
};

export default function AverageTimeToActionByPriority(
  props: AverageTimeToActionByPrioritytProps
): ReactElement {
  const { clientUid, reportStart, reportEnd } = props;

  const { t } = useTranslation('translation');

  const getTimeToActionByPriority = (): VizState => {
    return {
      chartType: 'table',
      pivotConfig: {
        x: ['security_event.priority'],
        y: ['measures'],
        fillMissingDates: true
      },
      query: {
        measures: ['security_event_extended.avg_time_to_action_minutes'],
        dimensions: ['security_event.priority'],
        timeDimensions: [],
        order: {
          'security_event.priority': 'asc'
        },
        filters: [
          {
            member: 'security_event.clientUid',
            operator: 'equals',
            values: [clientUid]
          },
          {
            member: 'security_event.createdDate',
            operator: 'inDateRange',
            values: [reportStart, reportEnd]
          },
          {
            member: 'security_event.priority',
            operator: 'notEquals',
            values: ['P5', '5']
          }
        ]
      }
    };
  };
  const getOverallTimeToActionByPriority = (): VizState => {
    return {
      chartType: 'table',
      pivotConfig: {
        x: [],
        y: ['measures'],
        fillMissingDates: true
      },
      query: {
        measures: ['security_event_extended.avg_time_to_action_minutes'],
        timeDimensions: [],
        filters: [
          {
            member: 'security_event.clientUid',
            operator: 'equals',
            values: [clientUid]
          },
          {
            member: 'security_event.createdDate',
            operator: 'inDateRange',
            values: [reportStart, reportEnd]
          },
          {
            member: 'security_event.priority',
            operator: 'notEquals',
            values: ['P5', '5']
          }
        ]
      }
    };
  };

  const { resultSet: timeToActionByPriorityData } = useCubeQuery(
    getTimeToActionByPriority().query!
  );
  const { resultSet: overallAverageData } = useCubeQuery(getOverallTimeToActionByPriority().query!);

  const timeToActionData = () => {
    const results = timeToActionByPriorityData?.chartPivot(getTimeToActionByPriority().pivotConfig);
    const data: { x: string; y: number; series: string }[] = [];
    let seriesName = '';

    timeToActionByPriorityData
      ?.seriesNames(getTimeToActionByPriority().pivotConfig)
      .forEach(({ key }) => {
        seriesName = key;
        results &&
          data.push(
            ...results.map(result => ({
              x: t(result.x),
              y: result[key],
              series: key
            }))
          );
      });
    //include p1-p4
    if (!data.find(e => e.x === 'P1')) data.push({ x: 'P1', y: 0, series: seriesName });
    if (!data.find(e => e.x === 'P2')) data.push({ x: 'P2', y: 0, series: seriesName });
    if (!data.find(e => e.x === 'P3')) data.push({ x: 'P3', y: 0, series: seriesName });
    if (!data.find(e => e.x === 'P4')) data.push({ x: 'P4', y: 0, series: seriesName });

    return data.sort((a, b) => a.x.localeCompare(b.x));
  };

  function getOverallAverage(resultSet: ResultSet<any>) {
    return formatDateTimeChart(
      resultSet.chartPivot()[0]['security_event_extended.avg_time_to_action_minutes'],
      false
    );
  }

  const myId = useId();
  useEffect(() => {
    if (!window['chartRenders']) window['chartRenders'] = {};
    window['chartRenders'][myId] = 'Rendering';
    return () => {
      window['chartRenders'] && delete window['chartRenders'][myId];
    };
  }, []);

  return (
    <>
      <h2 className="text-uppercase">Average Time to Action by Priority Level</h2>

      <figure>
        {' '}
        <Typography.Title level={4}>
          Overall Average Time to Action:{' '}
          {overallAverageData ? getOverallAverage(overallAverageData) : <Spin />}
        </Typography.Title>
      </figure>
      <figure>
        {' '}
        {timeToActionByPriorityData ? (
          <Column
            {...{
              style: { maxWidth: 50 },
              scale: {
                y: {
                  type: 'linear'
                }
              },

              legend: false,
              label: {
                // AT EACH Y POINT
                formatter: val => formatDateTimeChart(val, false),
                text: val => (val.y === 0 ? '' : val.y),
                textBaseline: 'bottom',
                style: {
                  becomeWrap: true,
                  wordWrapWidth: 40,
                  wordWrap: true
                }
              },
              ...getPriorityScaleColors(),
              theme: {
                type: 'threatadvisor'
              },

              tooltip: false,
              axis: {
                // ACROSS THE Y Axis... vertical in this case
                y: {
                  labelFormatter: (text: string, item: any, index: number) => {
                    return formatDateTimeChart(text, true);
                  }
                }
              }
            }}
            data={timeToActionData()}
            xField="x"
            yField="y"
            seriesField="series"
            colorField="x"
          />
        ) : (
          <Spin />
        )}
        <figcaption>
          <Typography.Text type="secondary">
            Shows average time to action by priority level between{' '}
            {formatDate(new Date(reportStart))} and {formatDate(new Date(reportEnd))}.
          </Typography.Text>
        </figcaption>
      </figure>
    </>
  );
}
